import React, { useState, useEffect } from "react";
import axiosInstance from "../../axiosInstance";
import { useAuth } from "../../contexts/AuthContext";
import "./ProductApproval.scss";
import { toast } from "react-toastify";
import GeneralTable from "../../components/Table/GeneralTable";
import ButtonWithIcon from "../../components/ButtonwithIcon/ButtonWithIcon";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";
import { Icon } from "@iconify/react";
import MinutesOfMeetingManager from "../../components/EnquiryStageHistory/MinutesOfMeetingManager";
import IconButton from "../../components/IconButton/IconButton";

const ProductApproval = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [approvalData, setApprovalData] = useState({
    operationStages: [],
  });
  const [momModal, setMomModal] = useState({
    isOpen: false,
    enquiryId: null,
    enquiryStageHistoryId: null,
  });

  useEffect(() => {
    if (user) {
      fetchPendingOrders();
    }
  }, [user]);

  const fetchPendingOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_PROD_MANAGER}/product-order-history/pending`,
      );

      if (response.data.status === 200 && response.data.data) {
        setOrders(response.data.data);
      } else {
        setError("Failed to fetch pending orders");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch pending orders");
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditOrder = (order) => {
    setCurrentOrder(order);
    const initialApprovalData = {
      orderHistoryId: order.id,
      operationStages: order.operationStages.map((stage) => ({
        operationStageId: stage.id,
        operationCheckListIds: stage.checkLists.map((cl) => cl.id), // Check all by default
        customSpecifications: [],
      })),
    };
    setApprovalData(initialApprovalData);
    setShowApprovalModal(true);
  };

  const handleQuickApprove = async (order) => {
    setLoading(true);
    try {
      const quickApprovalData = {
        orderHistoryId: order.id,
        operationStages: order.operationStages.map((stage) => ({
          operationStageId: stage.id,
          operationCheckListIds: stage.checkLists.map((cl) => cl.id), // Check all
          customSpecifications: [],
        })),
      };

      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_PROD_MANAGER}/product-orders/approve`,
        quickApprovalData,
      );

      if (response.data.status === 200) {
        await fetchPendingOrders();
        toast.success("Order approved successfully!");
      }
    } catch (error) {
      console.error("Error approving order:", error);
    } finally {
      setLoading(false);
    }
  };

  const submitApproval = async () => {
    if (!currentOrder) return;

    setLoading(true);
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_PROD_MANAGER}/product-orders/approve`,
        approvalData,
      );

      if (response.data.status === 200) {
        await fetchPendingOrders();
        toast.success("Order approved successfully!");
        setShowApprovalModal(false);
        setCurrentOrder(null);
        setApprovalData({ operationStages: [] });
      }
    } catch (error) {
      console.error("Error approving order:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkApproval = async () => {
    if (selectedOrders.size === 0) {
      toast.warning("Please select orders to approve");
      return;
    }

    const bulkApprovalData = {
      approvals: Array.from(selectedOrders).map((orderId) => {
        const order = orders.find((o) => o.id === orderId);
        return {
          orderHistoryId: orderId,
          operationStages: order.operationStages.map((stage) => ({
            operationStageId: stage.id,
            operationCheckListIds: stage.checkLists.map((cl) => cl.id), // Check all
            customSpecifications: [],
          })),
        };
      }),
    };

    setLoading(true);
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_PROD_MANAGER}/product-orders/approve/bulk`,
        bulkApprovalData,
      );

      if (response.data.status === 200) {
        await fetchPendingOrders();
        toast.success(`${selectedOrders.size} orders approved successfully!`);
        setSelectedOrders(new Set());
      }
    } catch (error) {
      console.error("Error bulk approving orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOrder = (orderId) => {
    const newSelected = new Set(selectedOrders);
    if (newSelected.has(orderId)) {
      newSelected.delete(orderId);
    } else {
      newSelected.add(orderId);
    }
    setSelectedOrders(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedOrders.size === orders.length) {
      setSelectedOrders(new Set());
    } else {
      setSelectedOrders(new Set(orders.map((order) => order.id)));
    }
  };

  const renderProductInfo = (value, row) => {
    const order = row.productInfo;

    return (
      <div className="product-info-cell">
        <div className="product-main">
          <input
            type="checkbox"
            checked={selectedOrders.has(order.id)}
            onChange={() => handleSelectOrder(order.id)}
            onClick={(e) => e.stopPropagation()}
            className="order-checkbox"
          />

          <div className="product-details">
            {/* PRODUCT NAME */}
            <span className="product-name">{order.productName}</span>

            {/* PRODUCT CODE */}
            {order.productCode && (
              <span className="product-code">
                Code: <strong>{order.productCode}</strong>
              </span>
            )}

            {/* PURCHASE ORDER */}
            {order.purchaseOrderCode && (
              <span className="product-code">
                PO: <strong>{order.purchaseOrderCode}</strong>
              </span>
            )}

            {/* CLIENT */}
            {order.clientName && (
              <span className="product-code">
                Client: <strong>{order.clientName}</strong>
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const handleOpenMOM = (order) => {
    if (!order.enquiryId) {
      toast.error("Missing enquiry information. Cannot open MOM.");
      return;
    }

    setMomModal({
      isOpen: true,
      enquiryId: order.enquiryId,
      enquiryStageHistoryId: null,
    });
  };

  const handleCloseMOM = () => {
    setMomModal({
      isOpen: false,
      enquiryId: null,
      enquiryStageHistoryId: null,
    });
  };

  const renderQuantity = (value, row) => {
    const order = row.quantity;
    return (
      <div className="quantity-cell">
        <div className="qty-row">
          <Icon icon="mdi:package-variant" className="icon" />
          <span className="value">{order.quantity}</span>
        </div>
      </div>
    );
  };

  const renderOperationStages = (value, row) => {
    const stages = row.operationStages;
    if (!stages || stages.length === 0)
      return <span className="no-data">No stages</span>;

    const totalChecklists = stages.reduce(
      (sum, stage) => sum + (stage.checkLists?.length || 0),
      0,
    );

    const mandatoryChecklists = stages.reduce(
      (sum, stage) =>
        sum + (stage.checkLists?.filter((cl) => cl.isMandatory).length || 0),
      0,
    );

    return (
      <div className="workflow-line">
        {/* PIPELINE */}
        <div className="workflow-stages">
          {stages.map((stage, i) => (
            <React.Fragment key={stage.id}>
              <span className="workflow-stage">{stage.name}</span>
              {i < stages.length - 1 && (
                <span className="workflow-arrow">→</span>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* SUMMARY */}
        <div className="workflow-meta">
          {stages.length} stages • {totalChecklists} checks
          {mandatoryChecklists > 0 && (
            <span className="workflow-required">
              {" "}
              • {mandatoryChecklists} required
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderSpecifications = (value, row) => {
    const specifications = row.specificationHistories;
    if (!specifications || specifications.length === 0) {
      return <span className="no-data">No specifications</span>;
    }
    return (
      <div className="specifications-cell">
        {specifications.slice(0, 2).map((spec, index) => (
          <div key={index} className="spec-item">
            <span className="spec-name">{spec.specificationName}:</span>
            <span className="spec-value">
              {spec.specificationValue} {spec.unit}
            </span>
          </div>
        ))}
        {specifications.length > 2 && (
          <span className="more-specs">+{specifications.length - 2} more</span>
        )}
      </div>
    );
  };

  const renderNotes = (value, row) => {
    const remarks = row.remarks;
    if (!remarks || remarks.trim() === "") {
      return <span className="no-data">No Remarks</span>;
    }
    return (
      <div className="notes-cell">
        <p className="notes-text">{remarks}</p>
      </div>
    );
  };

  const renderActionButtons = (value, row) => {
    const order = row.actions;

    return (
      <div className="action-buttons compact">
        <IconButton
          icon="mdi:message-text"
          variant="info"
          onClick={(e) => {
            e.stopPropagation();
            handleOpenMOM(order);
          }}
          title="MOM"
        />

        <IconButton
          icon="mdi:pencil"
          variant="edit"
          onClick={(e) => {
            e.stopPropagation();
            handleEditOrder(order);
          }}
          title="Edit"
        />

        <IconButton
          icon="mdi:check-circle"
          variant="add"
          onClick={(e) => {
            e.stopPropagation();
            handleQuickApprove(order);
          }}
          title="Approve"
        />
      </div>
    );
  };

  const ordersColumns = [
    {
      header: (
        <div className="header-with-checkbox">
          <input
            type="checkbox"
            checked={selectedOrders.size === orders.length && orders.length > 0}
            onChange={handleSelectAll}
            onClick={(e) => e.stopPropagation()}
          />
          <span>Product Details</span>
        </div>
      ),
      accessor: "productInfo",
      render: renderProductInfo,
      width: "18%",
    },
    {
      header: "Quantity",
      accessor: "quantity",
      render: renderQuantity,
      width: "10%",
    },
    {
      header: "Operation Workflow",
      accessor: "operationStages",
      render: renderOperationStages,
      width: "28%",
    },
    {
      header: "Specifications",
      accessor: "specificationHistories",
      render: renderSpecifications,
      width: "18%",
    },
    {
      header: "Notes",
      accessor: "remarks",
      render: renderNotes,
      width: "14%",
    },
    {
      header: "Actions",
      accessor: "actions",
      render: renderActionButtons,
      type: "action",
      actionType: "custom",
      width: "12%",
    },
  ];

  const ordersData = orders.map((order) => ({
    ...order,
    productInfo: order,
    quantity: order,
    actions: order,
  }));

  const updateApprovalStageCheckList = (stageIndex, checkListId, checked) => {
    setApprovalData((prev) => {
      const newData = { ...prev };
      const stage = newData.operationStages[stageIndex];

      if (checked) {
        if (!stage.operationCheckListIds.includes(checkListId)) {
          stage.operationCheckListIds = [
            ...stage.operationCheckListIds,
            checkListId,
          ];
        }
      } else {
        stage.operationCheckListIds = stage.operationCheckListIds.filter(
          (id) => id !== checkListId,
        );
      }

      return newData;
    });
  };

  const updateCustomSpecification = (stageIndex, specIndex, value) => {
    setApprovalData((prev) => {
      const newData = { ...prev };
      const stage = newData.operationStages[stageIndex];

      if (!stage.customSpecifications) {
        stage.customSpecifications = [];
      }

      stage.customSpecifications[specIndex] = value;
      return newData;
    });
  };

  const addCustomSpecification = (stageIndex) => {
    setApprovalData((prev) => {
      const newData = { ...prev };
      const stage = newData.operationStages[stageIndex];

      if (!stage.customSpecifications) {
        stage.customSpecifications = [];
      }

      stage.customSpecifications.push("");
      return newData;
    });
  };

  const removeCustomSpecification = (stageIndex, specIndex) => {
    setApprovalData((prev) => {
      const newData = { ...prev };
      const stage = newData.operationStages[stageIndex];
      stage.customSpecifications = stage.customSpecifications.filter(
        (_, index) => index !== specIndex,
      );
      return newData;
    });
  };

  if (loading && orders.length === 0) {
    return <LoadingScreen />;
  }

  return (
    <div className="teams-container">
      <div className="header">
        <div className="header-left">
          <h1>Production Approvals</h1>
          <p className="subtitle">
            Review and approve pending production orders
          </p>
        </div>
        <div className="header-actions">
          <ButtonWithIcon
            icon="mdi:refresh"
            text="Refresh"
            onClick={fetchPendingOrders}
            disabled={loading}
            variant="secondary"
          />
          <ButtonWithIcon
            icon="mdi:check-all"
            text={`Bulk Approve (${selectedOrders.size})`}
            onClick={handleBulkApproval}
            disabled={selectedOrders.size === 0 || loading}
          />
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)} className="close-error">
            ×
          </button>
        </div>
      )}

      <div className="tab-content">
        <GeneralTable
          columns={ordersColumns}
          data={ordersData}
          title="Pending Product Orders"
        />
      </div>

      {/* Approval Modal */}
      {showApprovalModal && currentOrder && (
        <div className="modal-overlay">
          <div className="approval-modal">
            <div className="modal-header">
              <div>
                <h3>Edit & Approve Order</h3>
                <p className="modal-subtitle">
                  {currentOrder.productName} ({currentOrder.productCode})
                </p>
              </div>
              <button
                className="close-modal"
                onClick={() => setShowApprovalModal(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="order-info-grid">
                <div className="info-item">
                  <Icon icon="mdi:package-variant" />
                  <div>
                    <span className="info-label">Quantity</span>
                    <span className="info-value">{currentOrder.quantity}</span>
                  </div>
                </div>
                {currentOrder.remarks && (
                  <div className="info-item full-width">
                    <Icon icon="mdi:note-text" />
                    <div>
                      <span className="info-label">Remarks</span>
                      <span className="info-value">{currentOrder.remarks}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="operation-stages">
                <h4>
                  <Icon icon="mdi:timeline-check" />
                  Operation Stages
                </h4>
                {currentOrder.operationStages.map((stage, stageIndex) => (
                  <div key={stage.id} className="stage-section">
                    <div className="stage-header">
                      <span className="stage-number">{stageIndex + 1}</span>
                      <div>
                        <h5>{stage.name}</h5>
                        {stage.description && (
                          <p className="stage-description">
                            {stage.description}
                          </p>
                        )}
                      </div>
                    </div>

                    {stage.checkLists && stage.checkLists.length > 0 && (
                      <div className="checklists">
                        <h6>
                          <Icon icon="mdi:checkbox-marked-outline" />
                          Checklist Items
                        </h6>
                        {stage.checkLists.map((checkList) => (
                          <div key={checkList.id} className="checklist-item">
                            <label>
                              <input
                                type="checkbox"
                                checked={approvalData.operationStages[
                                  stageIndex
                                ]?.operationCheckListIds.includes(checkList.id)}
                                onChange={(e) =>
                                  updateApprovalStageCheckList(
                                    stageIndex,
                                    checkList.id,
                                    e.target.checked,
                                  )
                                }
                              />
                              <span>{checkList.itemName}</span>
                              {checkList.isMandatory && (
                                <span className="mandatory">*Required</span>
                              )}
                            </label>
                            {checkList.description && (
                              <p className="checklist-description">
                                {checkList.description}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="custom-specifications">
                      <div className="spec-header">
                        <h6>Custom Specifications</h6>
                        <button
                          type="button"
                          onClick={() => addCustomSpecification(stageIndex)}
                          className="add-spec-btn"
                        >
                          <Icon icon="mdi:plus" />
                          Add Custom Specification
                        </button>
                      </div>
                      {approvalData.operationStages[
                        stageIndex
                      ]?.customSpecifications?.map((spec, specIndex) => (
                        <div key={specIndex} className="spec-input-group">
                          <input
                            type="text"
                            value={spec}
                            onChange={(e) =>
                              updateCustomSpecification(
                                stageIndex,
                                specIndex,
                                e.target.value,
                              )
                            }
                            placeholder="Enter custom specification"
                          />
                          <button
                            type="button"
                            onClick={() =>
                              removeCustomSpecification(stageIndex, specIndex)
                            }
                            className="remove-spec-btn"
                          >
                            <Icon icon="mdi:close" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="modal-footer">
              <ButtonWithIcon
                icon="mdi:close"
                text="Cancel"
                onClick={() => setShowApprovalModal(false)}
                className="cancel-button"
              />
              <ButtonWithIcon
                icon="mdi:check-circle"
                text={loading ? "Approving..." : "Approve Order"}
                onClick={submitApproval}
                disabled={loading}
                className="submit-button"
              />
            </div>
          </div>
        </div>
      )}

      {/* MOM Modal */}
      {momModal.isOpen && momModal.enquiryId && (
        <div className="modal-overlay">
          <div
            className="mom-modal-container"
            style={{ maxWidth: "95vw", width: "1400px", maxHeight: "90vh" }}
          >
            <MinutesOfMeetingManager
              enquiryId={momModal.enquiryId}
              enquiryStageHistoryId={momModal.enquiryStageHistoryId}
              axiosInstance={axiosInstance}
              getApiEndpoint={(endpoint) =>
                `${process.env.REACT_APP_API_PROD_MANAGER}${endpoint}`
              }
              onClose={handleCloseMOM}
              readOnly={true}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductApproval;
